import { useEffect, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faUpload } from '@fortawesome/free-solid-svg-icons';
import _ from 'lodash';
import i18n from 'i18n';
import { SearchBar } from 'components/common/SearchBar';
import { ListOperationArea } from 'components/ListOperationArea/ListOperationArea';
import styles from './deviceManagement.module.scss';

/**
 * 搜尋送出的節流間隔。與 StickableTanstackTable 在 `remote` 模式下用的值一致 ——
 * 這一頁的搜尋是後端做的，每敲一個字送一次會把清單端點打爆。
 */
const SEARCH_DEBOUNCE_MS = 1000;

type DeviceListToolbarProps = {
  defaultSearchString?: string;
  onSearch: (searchString: string) => void;
  onOpenImport: () => void;
  onOpenExport: () => void;
  /**
   * 還沒選銷售渠道。兩個操作都以渠道為母體，這時按下去只會什麼都不發生，
   * 使用者看不出是還沒選渠道，只看得出按鈕沒反應。
   */
  disabled?: boolean;
};

/**
 * 兩個分頁共用的操作列。
 *
 * 容器與搜尋列都是共用的那兩支（`ListOperationArea` / `SearchBar`），這裡只決定
 * 子項怎麼分組 —— 而分組正是 `ListOperationAreaWithSearchBar` 不讓呼叫端決定的部分：
 * 它把搜尋列與傳進去的按鈕排成同一層的並列子項，而容器是 `justify-content:
 * space-between`，所以三個子項會變成「左、中、右」，匯出鈕落在正中間。
 * 設計稿要的是匯入在左，右邊一個直欄、搜尋列在上、匯出在下，所以這裡給容器兩個子項。
 *
 * 匯入的名單與匯出的範圍都是整頁的條件，兩個分頁看到的操作因此完全相同。
 */
export const DeviceListToolbar: React.FC<DeviceListToolbarProps> = ({
  defaultSearchString,
  onSearch,
  onOpenImport,
  onOpenExport,
  disabled = false
}) => {
  const debouncedSearch = useMemo(
    () => _.debounce(onSearch, SEARCH_DEBOUNCE_MS),
    [onSearch]
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  return (
    <ListOperationArea>
      <Button
        variant='secondary'
        size='sm'
        className={styles.importButton}
        onClick={onOpenImport}
        disabled={disabled}
        data-testid='device-import-button'
      >
        <FontAwesomeIcon icon={faUpload} />
        <span className={styles.operationLabel}>
          {i18n.t('deviceManagement.buttons.import')}
        </span>
      </Button>
      <div className={styles.searchAndExport}>
        <SearchBar
          model={{
            placeholder: i18n.t('deviceManagement.placeholders.searchbar'),
            search: debouncedSearch,
            defaultValue: defaultSearchString
          }}
        />
        <Button
          variant='secondary'
          size='sm'
          onClick={onOpenExport}
          disabled={disabled}
          data-testid='device-export-button'
        >
          <FontAwesomeIcon icon={faDownload} />
          <span className={styles.operationLabel}>
            {i18n.t('deviceManagement.buttons.export')}
          </span>
        </Button>
      </div>
    </ListOperationArea>
  );
};
